import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../App';

export const useRealtime = (options = {}) => {
  const { API_BASE_URL } = useApp();
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  
  const {
    pollInterval = 5000, // 5 seconds
    maxEvents = 20,
    autoConnect = true
  } = options;

  const fetchEvents = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (lastUpdate) {
        params.append('since', lastUpdate);
      }
      
      const response = await fetch(`${API_BASE_URL}/realtime/events?${params}`);
      if (response.ok) {
        const data = await response.json();
        
        if (data.events && data.events.length > 0) {
          setEvents(prevEvents => {
            const newEvents = [...data.events, ...prevEvents];
            return newEvents.slice(0, maxEvents);
          });
        }
        
        setLastUpdate(data.lastUpdate);
        setIsConnected(true);
      }
    } catch (error) {
      console.error('Error fetching realtime events:', error);
      setIsConnected(false);
    }
  }, [API_BASE_URL, lastUpdate, maxEvents]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/realtime/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching realtime stats:', error);
    }
  }, [API_BASE_URL]);

  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  const addLocalEvent = useCallback((event) => {
    setEvents(prevEvents => [event, ...prevEvents.slice(0, maxEvents - 1)]);
  }, [maxEvents]);

  useEffect(() => {
    if (!autoConnect) return;

    let eventInterval, statsInterval;

    // Initial fetch
    fetchEvents();
    fetchStats();

    // Set up polling
    eventInterval = setInterval(fetchEvents, pollInterval);
    statsInterval = setInterval(fetchStats, pollInterval * 2); // Stats less frequently

    return () => {
      if (eventInterval) clearInterval(eventInterval);
      if (statsInterval) clearInterval(statsInterval);
    };
  }, [autoConnect, fetchEvents, fetchStats, pollInterval]);

  return {
    events,
    stats,
    isConnected,
    lastUpdate,
    clearEvents,
    addLocalEvent,
    refetch: () => {
      fetchEvents();
      fetchStats();
    }
  };
};

export default useRealtime; 